import { create } from 'zustand';

// Baby data structure for birth completion
export interface BabyData {
  weight_grams?: number;
  height_cm?: number;
  head_circumference_cm?: number;
  notes?: string;
  babies?: BabyInfo[];
}

export interface BabyInfo {
  name?: string;
  gender?: 'female' | 'male' | 'diverse';
  weight_grams?: number;
  height_cm?: number;
}

export interface Pregnancy {
  id: string;
  expected_due_date: string;
  child_number: number;
  child_count: number; // 1 = single, 2 = twins, etc.
  is_high_risk: boolean;
  baby_names: string[];
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Completion fields
  completion_type?: 'birth' | 'ended' | null;
  completion_date?: string | null;
  actual_birth_date?: string | null;
  birth_time?: string | null;
  baby_data?: BabyData | null;
}

// App mode types
export type AppMode = 'pregnancy' | 'idle' | 'baby';

interface PregnancyState {
  currentPregnancy: Pregnancy | null;
  lastCompletedPregnancy: Pregnancy | null;
  currentSSW: number | null;
  appMode: AppMode;
  /**
   * true, sobald appMode mindestens einmal von einer echten Quelle (fetchAppMode
   * ueber OnboardingGuard oder useAppMode) gesetzt wurde - im Unterschied zum
   * hartcodierten Default 'idle', den appMode bis dahin traegt. Konsumenten wie
   * ToolsPage nutzen das als Loading-Gate, um nicht mit dem Default zu rendern
   * und kurz danach auf den echten Wert umzuspringen.
   *
   * Wird nie zurueckgesetzt (auch nicht bei Logout) - passend zum bestehenden
   * Verhalten des Stores, der insgesamt nicht bei Logout zurueckgesetzt wird.
   */
  appModeResolved: boolean;
  isLoading: boolean;
  setPregnancy: (pregnancy: Pregnancy | null) => void;
  setLastCompletedPregnancy: (pregnancy: Pregnancy | null) => void;
  setAppMode: (mode: AppMode) => void;
  setLoading: (loading: boolean) => void;
  calculateSSW: () => void;
  getDaysSinceBirth: () => { days: number; weeks: number; years: number } | null;
}

export const usePregnancyStore = create<PregnancyState>((set, get) => ({
  currentPregnancy: null,
  lastCompletedPregnancy: null,
  currentSSW: null,
  appMode: 'idle',
  appModeResolved: false,
  isLoading: false,
  setPregnancy: (pregnancy) => {
    set({ currentPregnancy: pregnancy, isLoading: false });
    get().calculateSSW();
  },
  setLastCompletedPregnancy: (pregnancy) => {
    set({ lastCompletedPregnancy: pregnancy });
  },
  // setAppMode wird ausschliesslich mit einem tatsaechlich ermittelten Wert
  // aufgerufen (OnboardingGuard nach fetchAppMode, useAppMode im Erfolgs-,
  // No-User- und Fehlerfall) - nie als Platzhalter waehrend des Ladens. Daher
  // markiert jeder Aufruf appMode zugleich als "resolved".
  setAppMode: (mode) => {
    set({ appMode: mode, appModeResolved: true });
  },
  setLoading: (loading) => set({ isLoading: loading }),
  calculateSSW: () => {
    const { currentPregnancy } = get();
    if (!currentPregnancy) {
      set({ currentSSW: null });
      return;
    }

    // SSW calculation based on LMP (Last Menstrual Period)
    // LMP = Due Date - 280 days (standard pregnancy length)
    const dueDate = new Date(currentPregnancy.expected_due_date);
    const now = new Date();

    // Calculate LMP date (280 days = 40 weeks before due date)
    const lmpDate = new Date(dueDate.getTime() - (280 * 24 * 60 * 60 * 1000));

    // Days since LMP
    const daysSinceLMP = Math.floor((now.getTime() - lmpDate.getTime()) / (1000 * 60 * 60 * 24));

    // SSW = completed weeks since LMP + 1 (because week 1 starts at day 0)
    // Week 1 = days 0-6, Week 2 = days 7-13, etc.
    const currentSSW = Math.max(1, Math.min(42, Math.floor(daysSinceLMP / 7) + 1));

    set({ currentSSW });
  },
  getDaysSinceBirth: () => {
    const { lastCompletedPregnancy } = get();
    if (!lastCompletedPregnancy?.actual_birth_date) {
      return null;
    }

    const birthDate = new Date(lastCompletedPregnancy.actual_birth_date);
    const now = new Date();
    const diffTime = now.getTime() - birthDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return {
      days: diffDays,
      weeks: Math.floor(diffDays / 7),
      years: Math.floor(diffDays / 365),
    };
  },
}));
